/**
 * Assessment Blueprint Spec — Governance-side declarative blueprint.
 *
 * ARCHITECTURE v4.0 — S8 / FG-AssessmentPool · Layer C (mei 2026)
 *
 * WHY THIS LIVES IN governance/ (not assessment/):
 *  - DDD boundary: `governance/` is upstream of `assessment/`. The
 *    feasibility check needs a deterministic, pure model of "what does a
 *    Cito-style test require?" without dragging in any runtime assessment
 *    state, storage, or session machinery.
 *  - Single Source of Truth: this spec MIRRORS the targets that
 *    `assessment/utils/assessmentBlueprintGenerator.ts` aims for. The
 *    constants here are the contract; the generator implements it. When
 *    they drift, the governance pipeline will surface the drift as
 *    coach/info issues — exactly the observability we want.
 *
 * Design rules (anti-bandaid):
 *  - #1 No naked numbers: every target is a NAMED constant with rationale.
 *  - #2 No booleans where enums fit: feasibility status is a discriminated
 *       enum, never `boolean isFeasible`.
 *  - #4 Defaults are diagnostic, not punitive: Layer C findings are
 *       coach/info during shadow rollout. They will NEVER emit `blocker`
 *       through this stage.
 */

import type { ModuleId } from '../../content'
import { MODULES } from '../../content'
import type { GroupId } from '../../config/systemConfig'
import { getAllowedTiersForGroup } from '../../content/taxonomy/tiers'

// ─── Blueprint Targets ───────────────────────────────────────────────────────

/**
 * Default total number of questions in a Cito-style end-of-module test.
 * Mirrors the target consumed by `assessmentBlueprintGenerator`.
 */
export const BLUEPRINT_DEFAULT_QUESTION_COUNT = 30 as const

/**
 * Maximum number of questions that may be drawn from a single family in
 * one assembled test. Prevents family-overuse from masking pool problems
 * (a family with 30 MC-eligible questions does NOT make a 30-question
 * test feasible by itself — tests need breadth).
 */
export const MAX_QUESTIONS_PER_FAMILY = 2 as const

/**
 * Cito-style tier-band difficulty distribution within a single test.
 * These percentages are the contract `assessmentBlueprintGenerator` aims for.
 *
 * "low / mid / high" here refers to DIFFICULTY ZONES WITHIN A GROUP's allowed
 * tier range — NOT the global TierBand enum (basic/intermediate/advanced/expert)
 * used by Layer A. A group-6 test has its own low/mid/high split inside the
 * group-6 tier window.
 */
export const BLUEPRINT_TIER_BAND_SHARES = Object.freeze({
  low: 0.35,
  mid: 0.4,
  high: 0.25,
}) as Readonly<Record<BlueprintBand, number>>

export type BlueprintBand = 'low' | 'mid' | 'high'
export const BLUEPRINT_BANDS: readonly BlueprintBand[] = ['low', 'mid', 'high']

/**
 * Per-band target counts for the default 30-question test. Pre-computed
 * once so the simulator does not re-derive these on every call.
 */
export const BLUEPRINT_BAND_TARGETS: Readonly<Record<BlueprintBand, number>> =
  Object.freeze({
    low: Math.round(
      BLUEPRINT_DEFAULT_QUESTION_COUNT * BLUEPRINT_TIER_BAND_SHARES.low,
    ),
    mid: Math.round(
      BLUEPRINT_DEFAULT_QUESTION_COUNT * BLUEPRINT_TIER_BAND_SHARES.mid,
    ),
    high: Math.round(
      BLUEPRINT_DEFAULT_QUESTION_COUNT * BLUEPRINT_TIER_BAND_SHARES.high,
    ),
  })

// ─── Tier → Blueprint-Band Mapping ───────────────────────────────────────────

/**
 * Map a tier number to a BlueprintBand within a group's allowed tier range.
 * Splits the range into three contiguous thirds (low / mid / high).
 *
 * Out-of-range tiers return null — caller decides whether to skip or count
 * as "untiered for this group".
 */
export function tierToBlueprintBand(
  tier: number,
  allowedTiers: Set<number>,
): BlueprintBand | null {
  if (!allowedTiers.has(tier)) return null
  const sorted = Array.from(allowedTiers).sort((a, b) => a - b)
  const n = sorted.length
  if (n === 0) return null
  // Position within the sorted set, 0..n-1.
  const idx = sorted.indexOf(tier)
  const ratio = (idx + 0.5) / n
  if (ratio < BLUEPRINT_TIER_BAND_SHARES.low) return 'low'
  if (ratio < BLUEPRINT_TIER_BAND_SHARES.low + BLUEPRINT_TIER_BAND_SHARES.mid)
    return 'mid'
  return 'high'
}

// ─── Feasibility Inputs / Outputs ────────────────────────────────────────────

/**
 * Per-family availability snapshot for a single (group, module) combination.
 * Built by `assessmentHealthStage` from the SAME questions[] universe the
 * rest of the pipeline runs against — Layers A, B and C share one worldview.
 */
export interface FamilyAvailability {
  /** Family id (e.g. "REK-CIJF-PLUS"). */
  familyId: string
  /** Module the family belongs to. */
  module: ModuleId
  /**
   * MC-eligible questions in this family that ALSO have a tier in the
   * group's allowed set. Already pre-filtered by the caller.
   */
  mcEligibleByBand: Record<BlueprintBand, number>
  /** Total MC-eligible (in-band) count across all three bands. */
  totalMcEligible: number
  /**
   * A representative question id from this family for "open in Vragen-tab"
   * deeplinks. Optional — `null` if the family has zero usable questions.
   */
  representativeQuestionId: string | null
}

/** Discriminated enum — see anti-bandaid principle #2. */
export type FeasibilityStatus =
  | 'zero_questions'
  | 'shortfall'
  | 'degraded'
  | 'ok'

export interface FeasibilitySimulationResult {
  status: FeasibilityStatus
  /** Total questions the simulator could place in the test. */
  achievable: number
  /** Target total — currently always BLUEPRINT_DEFAULT_QUESTION_COUNT. */
  target: number
  /** Per-band achievable counts. */
  bandAchievable: Record<BlueprintBand, number>
  /** Per-band target counts. */
  bandTarget: Record<BlueprintBand, number>
  /** Per-band family ids contributing to the achievable count. */
  bandFamilies: Record<BlueprintBand, string[]>
  /** Bands with zero achievable questions — the loudest signal in 'degraded'. */
  emptyBands: BlueprintBand[]
  /** Representative question id for deeplinking (best-effort). */
  representativeQuestionId: string | null
}

// ─── Combo Iterator ──────────────────────────────────────────────────────────

/**
 * Enumerate every (group, module) combination Layer C should simulate.
 * 3 groups × N modules = 3N combos (currently 30 with 10 modules).
 *
 * Skips modules that don't declare a moduleId — defensive.
 */
export function listAssessmentCombos(): Array<{
  group: GroupId
  module: ModuleId
}> {
  const out: Array<{ group: GroupId; module: ModuleId }> = []
  const groups: GroupId[] = [6, 7, 8]
  for (const group of groups) {
    for (const m of MODULES) {
      if (!m?.id) continue
      out.push({ group, module: m.id as ModuleId })
    }
  }
  return out
}

// ─── Simulator ───────────────────────────────────────────────────────────────

/**
 * Pure feasibility simulator. Given a per-family availability snapshot
 * (already filtered to a group's allowed tiers), decide whether the
 * blueprint generator could assemble a complete `BLUEPRINT_DEFAULT_QUESTION_COUNT`
 * test honouring `MAX_QUESTIONS_PER_FAMILY` and the band targets.
 *
 * Algorithm (greedy, deterministic):
 *  1. For each band, sort families by availability desc.
 *  2. Pull up to MAX_QUESTIONS_PER_FAMILY from each family per band,
 *     stopping at the band target.
 *  3. Sum achievable across bands; classify status.
 *
 * This intentionally underestimates capacity vs. a perfect optimiser
 * (a family contributing to multiple bands is counted band-locally) —
 * the goal is to flag REAL feasibility risk, not to match the runtime
 * generator's exact placement.
 */
export function simulateBlueprintFeasibility(
  availabilities: readonly FamilyAvailability[],
  options?: { target?: number },
): FeasibilitySimulationResult {
  const target = options?.target ?? BLUEPRINT_DEFAULT_QUESTION_COUNT
  const bandTarget: Record<BlueprintBand, number> = {
    low: Math.round(target * BLUEPRINT_TIER_BAND_SHARES.low),
    mid: Math.round(target * BLUEPRINT_TIER_BAND_SHARES.mid),
    high: Math.round(target * BLUEPRINT_TIER_BAND_SHARES.high),
  }

  const bandAchievable: Record<BlueprintBand, number> = {
    low: 0,
    mid: 0,
    high: 0,
  }
  const bandFamilies: Record<BlueprintBand, string[]> = {
    low: [],
    mid: [],
    high: [],
  }

  let representativeQuestionId: string | null = null

  for (const band of BLUEPRINT_BANDS) {
    // Sort families by this band's availability, desc — stable on familyId
    // to keep the algorithm deterministic across pipeline runs.
    const sorted = [...availabilities]
      .filter((a) => a.mcEligibleByBand[band] > 0)
      .sort((a, b) => {
        const diff = b.mcEligibleByBand[band] - a.mcEligibleByBand[band]
        if (diff !== 0) return diff
        return a.familyId.localeCompare(b.familyId)
      })

    let placed = 0
    for (const fam of sorted) {
      if (placed >= bandTarget[band]) break
      const fromFamily = Math.min(
        MAX_QUESTIONS_PER_FAMILY,
        fam.mcEligibleByBand[band],
        bandTarget[band] - placed,
      )
      if (fromFamily <= 0) continue
      placed += fromFamily
      bandFamilies[band].push(fam.familyId)
      if (!representativeQuestionId && fam.representativeQuestionId) {
        representativeQuestionId = fam.representativeQuestionId
      }
    }
    bandAchievable[band] = placed
  }

  const achievable =
    bandAchievable.low + bandAchievable.mid + bandAchievable.high
  const emptyBands = BLUEPRINT_BANDS.filter((b) => bandAchievable[b] === 0)

  let status: FeasibilityStatus
  if (achievable === 0) {
    status = 'zero_questions'
  } else if (achievable < target) {
    status = 'shortfall'
  } else if (
    emptyBands.length > 0 ||
    BLUEPRINT_BANDS.some((b) => bandAchievable[b] < bandTarget[b])
  ) {
    // Hit the total target but at least one band is under its share.
    status = 'degraded'
  } else {
    status = 'ok'
  }

  return {
    status,
    achievable,
    target,
    bandAchievable,
    bandTarget,
    bandFamilies,
    emptyBands,
    representativeQuestionId,
  }
}

/**
 * Helper: classify a single tier into a band relative to a group's
 * allowed-tiers set. Convenience wrapper used by the stage when grouping
 * questions for the simulator input.
 */
export function classifyTierForGroup(
  tier: number,
  group: GroupId,
): BlueprintBand | null {
  return tierToBlueprintBand(tier, getAllowedTiersForGroup(group))
}
